import { useMemo } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useTripSearchLogic } from "hooks/useTripSearchLogic"

const MIN_SEARCH_CHARS = 3

export const useListingsDerivedData = ({ search, trips }) => {
  const { t } = useTranslation()
  const categories = useSelector((state) => state.trips.categories.data?.categories || [])

  const categoryOptions = useMemo(() => categories.map((c) => ({ value: c.id, label: c.name })), [categories])

  const durationOptions = useMemo(
    () => [
      { value: "single_day", label: t("experiences.filters.single_day", { defaultValue: "Single Day" }) },
      { value: "multi_day", label: t("experiences.filters.multi_day", { defaultValue: "Multi Day" }) }
    ],
    [t]
  )

  const useSearchTrips =
    (search.showSuggestions && search.debouncedSearchQuery.trim()) ||
    (search.debouncedSearchQuery.trim().length >= MIN_SEARCH_CHARS && !search.confirmedSearchQuery.trim())

  const { categorizedSearchResults } = useTripSearchLogic({
    trips: useSearchTrips ? search.searchTrips : trips,
    searchQuery: search.searchQuery,
    selectedTrip: search.selectedTrip,
    debouncedSearchQuery: search.debouncedSearchQuery,
    showSuggestions: search.showSuggestions
  })

  const categoryById = useMemo(
    () => Object.fromEntries(categories.map((cat) => [cat.id, cat])),
    [categories]
  )

  const enrichedTrips = useMemo(() => {
    if (!categories.length) return trips
    return trips.map((trip) => {
      const hasCategories = Array.isArray(trip.categories) && trip.categories.length > 0
      if (hasCategories) return trip
      const ids = trip.category_ids ?? (trip.category_id != null ? [trip.category_id] : [])
      if (ids.length === 0) return trip
      const resolved = ids.map((id) => categoryById[id]).filter(Boolean)
      return resolved.length ? { ...trip, categories: resolved } : trip
    })
  }, [trips, categories, categoryById])

  const tripsForMainList = useMemo(() => {
    if (search.selectedTrip) return [search.selectedTrip]
    if (search.confirmedSearchQuery.trim()) return search.searchTrips
    return enrichedTrips
  }, [search.selectedTrip, search.confirmedSearchQuery, search.searchTrips, enrichedTrips])

  return { categoryOptions, durationOptions, categorizedSearchResults, tripsForMainList }
}
